import type { JSX } from 'react';

import { HERO, INSURANCE_BAND, SUPPORT_POINTS } from './content.js';
import { EligibilityCta } from './eligibility-cta.js';
import { Band, BandHeading } from './sections.js';

/**
 * Hero: headline, how-it-works checklist, eligibility CTA, contact line.
 *
 * The photo is a full-bleed section BACKGROUND rather than an `<img>`, because the source asset is a
 * wide hero plate with the subject on the right and cream negative space baked into the left — the
 * same way the marketing site uses it. As an inline image the subject renders tiny inside its column.
 * Applied from `lg` up only: on narrow screens the text would sit on top of the subject.
 */
export function Hero(): JSX.Element {
  return (
    <section className="w-full bg-[var(--cream)] bg-right bg-no-repeat px-6 lg:bg-[image:url('/images/photo-hero.webp')] lg:bg-cover">
      <div className="mx-auto w-full max-w-[70rem]">
        <div className="grid items-center gap-10 py-12 sm:py-16 lg:min-h-[30rem] lg:grid-cols-[minmax(0,32rem)_1fr]">
          <div className="flex flex-col gap-5">
            <BandHeading level={1} lead={HERO.headingLead} accent={HERO.headingAccent} />
            <p className="max-w-[46ch] text-[1.05rem] leading-relaxed text-[var(--ink)]">
              <strong className="font-semibold">{HERO.leadStrong}</strong> {HERO.lead}
            </p>
            <ul className="flex flex-col gap-1.5 text-[var(--ink)]">
              {HERO.checklist.map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <span className="text-[var(--forest)]" aria-hidden="true">
                    ✓
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <EligibilityCta label={HERO.cta} />
            <p className="max-w-[52ch] text-sm leading-relaxed text-[var(--ink-soft)]">
              <strong className="font-semibold text-[var(--ink)]">Questions?</strong> {HERO.contact}
            </p>
          </div>
          {/* Right column is intentionally empty: it reserves space for the background subject. */}
          <div aria-hidden="true" />
        </div>
      </div>
    </section>
  );
}

/** Thin reassurance band directly under the hero. */
export function InsuranceBand(): JSX.Element {
  return (
    <div className="w-full bg-[var(--cream)] px-6 pb-10 text-center">
      <p className="text-sm font-semibold tracking-wide text-[var(--forest)] sm:text-base">
        {INSURANCE_BAND}
      </p>
    </div>
  );
}

/** Blush band: photo of a dietitian plus the four support points. */
export function PersonalizedSupport(): JSX.Element {
  return (
    <Band background="var(--tint-blush)">
      <div className="flex flex-col gap-10">
        <BandHeading
          lead="Personalized support from a "
          accent="registered dietitian"
          className="text-center"
        />
        <div className="grid items-center gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <img
            src="/images/photo-dietitian.webp"
            alt="Registered dietitian sitting at a desk smiling, next to fresh fruit"
            className="h-auto w-full rounded-[var(--radius-lg)] object-cover"
            width={620}
            height={620}
          />
          <ul className="flex flex-col gap-6">
            {SUPPORT_POINTS.map((point) => (
              <li key={point.title} className="flex gap-3">
                <img
                  src="/images/check-circle-red.svg"
                  alt=""
                  aria-hidden="true"
                  className="mt-0.5 size-6 shrink-0"
                  width={24}
                  height={24}
                />
                <div className="flex flex-col gap-1">
                  <span className="font-semibold text-[var(--coral)]">{point.title}</span>
                  <p className="nrx-prose text-[var(--ink)]">{point.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Band>
  );
}
